import { ExtendedLinkProps, Link } from "@xcorejs/ui";
import NextLink, { LinkProps } from "next/link";
import { PropsWithChildren, useMemo } from "react";
import { sentryLogger } from "@robe/core";

import { containsAnchor, resolvePathOrUrl } from "./utils";
import { useContentLink } from "./useContentLink";
import { LinkableContent } from "./types";

export * from "./init";
export * from "./types";

export interface ContentLinkProps
  extends Omit<LinkProps, "href" | "as" | "passHref" | "onClick" | "onMouseEnter" | "onTouchStart" | "content">,
    Omit<ExtendedLinkProps, "as" | "content"> {
  content: LinkableContent;
  /** Query params object. Object key is query param key and value is object param value */
  queries?: Record<string, string | string[] | number | number[]>;
  /** Anchor `#` that will appear at the end of the URL */
  anchor?: string | number;
  /** Locale from specified locales in the config */
  locale?: string;

  noUILink?: boolean;
  /** Content link wrapper className if `noUILink` is not used */
  uiLinkClassname?: string;
  /** Content link is flex by default */
  inline?: boolean;
}

/**
 * Resolver component for contents links built on top of `next/link`.
 *
 * Use `noUILink` if link is not a simple text,
 * but keep in mind that all styles need to be moved to `style` prop because next link does not support css in js.
 * If invalid values or children that violate HTML structure are provided component won't be rendered and error will be logged to sentry.
 */
export const ContentLink = ({
  content,
  queries,
  anchor,
  locale,

  // next/link props
  replace,
  scroll,
  shallow,
  prefetch = false,

  // react props
  children,
  onClick,

  // style
  noUILink,
  className,
  uiLinkClassname,
  inline,

  ...props
}: PropsWithChildren<ContentLinkProps>) => {
  const { href, as } = useContentLink();

  if (!content) {
    sentryLogger({
      error: new Error("Content in <ContentLink /> component is falsy"),
      throwError: true,
      context: {
        locale,
        content,
        ...(queries && { queries }),
        ...(anchor && { anchor }),
      },
    });
  }

  const linkValue = useMemo(
    () => ({
      href: href(content, { queries, anchor })?.replace("/index", "/") ?? "/404",
      as: as(content, { queries, anchor }) ?? "/404",
    }),
    [as, content, href, queries, anchor]
  );

  if (!locale && !linkValue.href) {
    sentryLogger({
      error: new Error("NextLink href value is falsy in <ContentLink /> component"),
      context: {
        linkValue,
        locale,
        content,
        ...(queries && { queries }),
        ...(anchor && { anchor }),
      },
    });

    return null;
  }

  // Prevent hydration errors
  if (containsAnchor(children)) {
    sentryLogger({
      error: new Error("HTML violation, <a></a> was provided in <ContentLink /> children"),
      context: {
        linkValue,
        locale,
        content,
        ...(queries && { queries }),
        ...(anchor && { anchor }),
      },
    });

    return null;
  }

  return !locale ? (
    <NextLink
      href={linkValue.href}
      // Next link `as` only accepts relative paths
      as={resolvePathOrUrl(linkValue.as)}
      passHref
      scroll={scroll}
      replace={replace}
      shallow={shallow}
      prefetch={prefetch}
      {...(uiLinkClassname ? { className: uiLinkClassname } : noUILink && className && { className })}
      style={{ ...(!inline && { alignItems: "center", display: "flex" }), ...props?.style, color: "inherit" }}
      onClick={onClick}
    >
      {noUILink ? (
        children
      ) : (
        <Link as="span" {...props} {...(className && { className })}>
          {children}
        </Link>
      )}
    </NextLink>
  ) : (
    <Link {...{ ...props, onClick, className, href: as(content, { queries, anchor, locale }) as any }}>{children}</Link>
  );
};
